import { BeakerIcon, BuildingOfficeIcon, ChevronDownIcon, ExclamationTriangleIcon, ForwardIcon } from '@heroicons/react/24/outline';
import { useFetcher } from '@remix-run/react';
import { GQLOrderFragment, GQLStoreFragment } from '@theprintbar/graphql';
import { cn, getOrderTurnaroundStatus, objectToFormData, orderHasCompleted } from '@theprintbar/shared';
import { useCallback } from 'react';
import { Dropdown, DropdownOption, LightningBolt, Pill, ShopifyIcon, getOrderStatusColour, getOrderStatusName } from '../../../components';

interface OrderPillsProps {
	order: GQLOrderFragment;
	store?: GQLStoreFragment;
	warehouseURL?: string;
}

const orderPriorities = [
	{
		value: 1,
		colour: 'bg-white',
	},
	{
		value: 2,
		colour: 'bg-pink',
	},
	{
		value: 3,
		colour: 'bg-yellow',
	},
	{
		value: 10,
		colour: 'bg-blue-bright',
	},
];

export function OrderPills({ order, store, warehouseURL }: OrderPillsProps) {
	const priorityColour = orderPriorities.find(priority => priority.value === order.priority)?.colour || 'bg-white';
	const dueStatus = getOrderTurnaroundStatus(order);

	const fetcher = useFetcher();

	const handlePriorityChange = useCallback((priority: number) => {
		fetcher.submit(
			objectToFormData({
				action: 'updateOrderPriority',
				priority,
			}),
			{
				method: 'POST',
			},
		);
	}, []);

	return (
		<div className="flex ml-4 items-start flex-wrap gap-1 lg:gap-2 tracking-normal font-sans">
			{warehouseURL && (
				<>
					{order.internalId ? (
						<>
							<a href={`${warehouseURL}/orders/${order.internalId}`} target="_blank" rel="noreferrer">
								<Pill className="bg-blue text-white py-0.5 px-1">
									<BuildingOfficeIcon className="w-4 h-4 mr-1" />
									{order.internalId}
								</Pill>
							</a>
							<Pill className={cn('py-0.5 px-1 flex items-center', priorityColour)}>
								{order.priority === 1 && <LightningBolt className="w-3 h-3 lg:w-4 lg:h-4" />}
								Priority {order.priority}
							</Pill>
						</>
					) : (
						<Dropdown
							buttonElement={(selectedOption: DropdownOption): JSX.Element => (
								<Pill className={cn('py-0.5 px-1 flex items-center', selectedOption.value.colour)}>
									{selectedOption.value.value === 1 && <LightningBolt className="w-3 h-3 lg:w-4 lg:h-4" />}
									{selectedOption.label}
									<ChevronDownIcon className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
								</Pill>
							)}
							initialValue={{
								value: orderPriorities.find(priority => priority.value === order.priority) || orderPriorities[0],
								label: `Priority ${order.priority}`,
							}}
							options={[
								...orderPriorities.map(status => ({
									value: status,
									label: `Priority ${status.value}`,
								})),
							]}
							onChange={({ value }) => handlePriorityChange(value)}
							className="text-xs"
							panelClassName="w-full py-1"
							optionClassName={(focused, selected) =>
								focused || selected ? 'bg-blue/10 whitespace-nowrap py-1 px-3' : 'text-blue whitespace-nowrap py-1 px-3'
							}
						/>
					)}
					{!!store && (
						<Pill className="bg-orange text-white py-0.5 px-1 flex items-center">
							<ForwardIcon className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
							Partner
						</Pill>
					)}
				</>
			)}
			{order.referenceName && (
				<Pill className="bg-white text-blue py-0.5 px-1">
					<ShopifyIcon className="w-4 h-4 mr-1" /> {order.referenceName}
				</Pill>
			)}
			{order.sameday && !order.store && (
				<Pill className="py-0.5 px-1 flex items-center bg-blue-bright">
					<LightningBolt className="w-3 h-3 lg:w-4 lg:h-4" />
					Same Day
				</Pill>
			)}
			{order.sample && (
				<Pill className="bg-purple-dark text-white py-0.5 px-1 flex items-center">
					<BeakerIcon className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
					Sample
				</Pill>
			)}

			{order.reprint && (
				<Pill className="bg-red text-white py-0.5 px-1 flex items-center">
					<ExclamationTriangleIcon className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
					Reprint
				</Pill>
			)}
			<Pill className={cn('py-0.5 px-1 text-white', getOrderStatusColour(order.status))}>{getOrderStatusName(order)}</Pill>
			{!orderHasCompleted(order) && (
				<Pill className={`${dueStatus?.className} text-xs font-sans py-1 px-2`}>Due in: {dueStatus?.text}</Pill>
			)}
		</div>
	);
}
